import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import polarisEn from "@shopify/polaris/locales/en.json";
import { AuthProvider } from './contexts/AuthContext';
import { ClientProvider } from './contexts/ClientContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CallLogs from './pages/CallLogs';
import ImportJobs from './pages/ImportJobs';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import AiPromptManagement from './pages/AiPromptManagement';
import Billing from './pages/Billing';
import Usage from './pages/Usage';
import TenantSelection from './pages/TenantSelection';
// import AiSettings from './pages/AiSettings';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginRedirect from './components/LoginRedirect';
import ClientSelectionModal from './components/ClientSelectionModal';
import { useAuth } from './contexts/AuthContext';
import { useClient } from './contexts/ClientContext';

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const { selectedClient, hasFetched } = useClient();
  const [showClientModal, setShowClientModal] = useState(false);
  const location = useLocation();

  // Force tenant selection for support managers
  const isSupportManagerWithoutTenant = user?.role === 'support_manager' && !selectedClient;
  const isOnTenantSelectionPage = location.pathname === '/tenant-selection';

  // Redirect support managers without tenant to tenant selection page
  useEffect(() => {
    if (isSupportManagerWithoutTenant && !isOnTenantSelectionPage) {
      // Don't redirect if we're already on the tenant selection page
      return;
    }
  }, [isSupportManagerWithoutTenant, isOnTenantSelectionPage]);

  // Show client selection modal for support managers who haven't selected a client (legacy)
  useEffect(() => {
    if (user?.role === 'support_manager' && hasFetched && !selectedClient && !isOnTenantSelectionPage) {
      setShowClientModal(true);
    }
  }, [user?.role, hasFetched, selectedClient, isOnTenantSelectionPage]);

  return (
    <>
      <Routes>
        <Route path="/login" element={
          <LoginRedirect>
            <Login />
          </LoginRedirect>
        } />
        
        {/* Tenant selection route for support managers */}
        <Route path="/tenant-selection" element={
          <ProtectedRoute allowedRoles={['support_manager']}>
            <TenantSelection />
          </ProtectedRoute>
        } />
        
        <Route path="/" element={
          <ProtectedRoute>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Dashboard /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/call-logs" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><CallLogs /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/import-jobs" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><ImportJobs /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/ai-prompt-management" element={
          <ProtectedRoute allowedRoles={['support_manager']}>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><AiPromptManagement /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute allowedRoles={['support_manager', 'admin']}>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Billing /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/usage" element={
          <ProtectedRoute allowedRoles={['support_manager', 'admin']}>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Usage /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Settings /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Profile /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="/ai-settings" element={
          <ProtectedRoute>
            {isSupportManagerWithoutTenant ? (
              <Navigate to="/tenant-selection" replace />
            ) : (
              <Layout><Settings /></Layout>
            )}
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {user?.role === 'support_manager' && (
        <ClientSelectionModal
          open={showClientModal}
          onClose={() => setShowClientModal(false)}
        />
      )}
    </>
  );
};

const AppWrapper: React.FC = () => {
  const { loading: authLoading } = useAuth();
  
  if (authLoading) {
    return null; // Show nothing while auth is loading
  }
  
  return <AppContent />;
};

const App: React.FC = () => {
  return (
    <AppProvider i18n={polarisEn}>
      <Router>
        <AuthProvider>
          <ClientProvider>
            <AppWrapper />
          </ClientProvider>
        </AuthProvider>
      </Router>
    </AppProvider>
  );
};

export default App;
